import { useParams, Link } from 'react-router-dom';
import { useState, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import SchemaOrg from '@/components/base/SchemaOrg';
import { categories, hotVideos, latestVideos, platforms } from '@/mocks/data';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [selectedPlatform, setSelectedPlatform] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('hot');

  const currentCategory = categories.find((c) => c.slug === slug) || categories[0];

  const allVideos = [...hotVideos, ...latestVideos].filter(
    (v, i, arr) => arr.findIndex((x) => x.id === v.id) === i
  );

  const filteredVideos = useMemo(() => {
    let videos = allVideos.filter((v) => v.category === currentCategory.name);
    if (selectedPlatform !== 'all') {
      const platform = platforms.find((p) => p.slug === selectedPlatform);
      if (platform) {
        videos = videos.filter((v) => v.platform === platform.name);
      }
    }
    if (sortBy === 'new') {
      videos = [...videos].reverse();
    } else if (sortBy === 'rating') {
      videos = [...videos].sort((a, b) => b.rating - a.rating);
    }
    return videos;
  }, [allVideos, currentCategory.name, selectedPlatform, sortBy]);

  return (
    <>
      <ScrollToTop />
      <SchemaOrg
        type="WebPage"
        data={{
          name: `${currentCategory.name} - 小蓝视频`,
          url: `https://www.xiaolanvideo.com/category/${slug}`,
          description: `小蓝视频${currentCategory.name}频道，高清免费在线观看最新${currentCategory.name}，包含香蕉视频、糖豆视频、茄子视频等平台优质内容。`,
        }}
      />

      <Navbar />

      <main className="pt-16">
        <div className="px-4 md:px-6 py-6">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: currentCategory.name },
              ]}
            />

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-accent-100 text-accent-600">
                  <i className={`${currentCategory.icon} text-2xl`}></i>
                </div>
                <div>
                  <h1 className="text-2xl md:text-3xl font-heading text-foreground-900">
                    {currentCategory.name}
                  </h1>
                  <p className="text-sm text-foreground-500">共 {filteredVideos.length} 部作品</p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setSortBy('hot')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    sortBy === 'hot'
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className="ri-fire-line mr-1.5"></i>最热
                </button>
                <button
                  onClick={() => setSortBy('new')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    sortBy === 'new'
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className="ri-timer-flash-line mr-1.5"></i>最新
                </button>
                <button
                  onClick={() => setSortBy('rating')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    sortBy === 'rating'
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className="ri-star-line mr-1.5"></i>评分
                </button>
              </div>
            </div>

            <div className="flex items-center gap-2 mb-6 flex-wrap">
              <span className="text-sm text-foreground-500 mr-1 whitespace-nowrap">平台：</span>
              <button
                onClick={() => setSelectedPlatform('all')}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  selectedPlatform === 'all'
                    ? 'bg-secondary-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                全部
              </button>
              {platforms.map((p) => (
                <button
                  key={p.slug}
                  onClick={() => setSelectedPlatform(p.slug)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    selectedPlatform === p.slug
                      ? 'bg-secondary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {p.name}
                </button>
              ))}
            </div>
          </div>
        </div>

        <section className="px-4 md:px-6 pb-12">
          <div className="max-w-7xl mx-auto">
            {filteredVideos.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {filteredVideos.map((video) => (
                  <Link
                    key={video.id}
                    to={`/article/${video.id}`}
                    className="group block bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                  >
                    <div className="aspect-video overflow-hidden relative">
                      <img
                        src={video.image}
                        alt={video.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      {video.quality && (
                        <span className="absolute top-3 left-3 text-xs px-2 py-0.5 bg-black/60 text-white rounded-md backdrop-blur-sm">
                          {video.quality}
                        </span>
                      )}
                      <span className="absolute top-3 right-3 text-xs px-2 py-0.5 bg-accent-500 text-white rounded-md font-medium">
                        {video.rating}
                      </span>
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                        <div className="w-14 h-14 flex items-center justify-center rounded-full bg-white/90 text-primary-500">
                          <i className="ri-play-fill text-2xl"></i>
                        </div>
                      </div>
                    </div>
                    <div className="p-4">
                      <h3 className="font-semibold text-foreground-900 text-sm md:text-base group-hover:text-primary-600 transition-colors duration-200 line-clamp-1">
                        {video.title}
                      </h3>
                      <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                        <span className="text-xs px-2 py-0.5 bg-secondary-100 text-secondary-700 rounded-md">
                          {video.platform}
                        </span>
                        <span className="text-xs text-foreground-400">{video.category}</span>
                        {(video as any).episode && (
                          <span className="text-xs text-primary-500 font-medium ml-auto">{(video as any).episode}</span>
                        )}
                      </div>
                      {video.views && (
                        <p className="text-xs text-foreground-400 mt-1.5">
                          <i className="ri-eye-line mr-1"></i>
                          {video.views} 次观看
                        </p>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-200 text-foreground-400">
                  <i className="ri-inbox-line text-3xl"></i>
                </div>
                <p className="mt-4 text-foreground-500">该分类暂无内容，请尝试其他筛选条件</p>
              </div>
            )}
          </div>
        </section>

        <div className="px-4 md:px-6 pb-12">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-foreground-500 mr-2 whitespace-nowrap">其他分类：</span>
              {categories
                .filter((c) => c.slug !== slug)
                .map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="px-4 py-2 bg-background-100 rounded-lg text-sm text-foreground-600 hover:bg-accent-100 hover:text-accent-700 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                  >
                    {cat.name}
                  </Link>
                ))}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}